import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { GripVertical, LayoutGrid, Plus, RotateCcw, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import ReactGridLayout, {
  useContainerWidth,
  type Layout,
  type LayoutItem,
} from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { useClinic } from '@/hooks/useClinicSettings';
import { useCurrentUserProfile } from '@/hooks/useCurrentUserProfile';
import { useDashboardLayout } from '@/hooks/useDashboardLayout';
import { buildResetLayout, shouldPersistDashboardGridChange } from '@/lib/dashboardLayoutStorage';
import { useGreetingNow } from '@/hooks/useGreetingNow';
import { readActivatedModules } from '@/lib/clinicModules';
import type { PermissionResource } from '@/lib/permissions';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { PageLayout } from '@/components/ui/PageLayout';
import { Skeleton } from '@/components/ui/Skeleton';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { greetingPeriod, type GreetingPeriod } from '@/utils/greeting';
import { DashboardWidgetBody } from '@/pages/dashboard/DashboardWidgets';
import { DashboardWidgetFrame } from '@/pages/dashboard/DashboardWidgetFrame';
import { DashboardNewMenu } from '@/pages/dashboard/DashboardNewMenu';
import { formatDashboardGreetingDate } from '@/pages/dashboard/dashboardFormat';
import {
  DASHBOARD_GRID_COLS,
  DASHBOARD_GRID_MARGIN,
  DASHBOARD_ROW_HEIGHT,
  displayColsForWidth,
  layoutForDisplay,
} from '@/pages/dashboard/dashboardLayout';
import {
  DEFAULT_DASHBOARD_LAYOUT,
  filterLayoutByAccess,
  filterPageLevelWidgets,
  isDashboardWidgetId,
  isPageLevelWidget,
  isWidgetAllowed,
  mergeVisibleLayout,
  nextWidgetPlacement,
  WIDGET_CATALOG,
  WIDGET_IDS,
  withCatalogConstraints,
  type DashboardWidgetId,
} from '@/pages/dashboard/widgetCatalog';

const STAFF_GREETING_FALLBACK: Record<GreetingPeriod, string> = {
  morning: 'Godmorgen',
  forenoon: 'God formiddag',
  afternoon: 'God eftermiddag',
  evening: 'God aften',
};

export function Dashboard() {
  const { t, i18n } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { hasPermission } = useAuth();
  const { data: profile } = useCurrentUserProfile();
  const { data: clinic } = useClinic(activeClinicId);
  const { storedLayout, hasSavedLayout, isLoading, saveLayout, resetLayout } =
    useDashboardLayout(activeClinicId);
  const { width, containerRef, mounted } = useContainerWidth();

  const [editing, setEditing] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draft, setDraft] = useState<LayoutItem[] | null>(null);

  useEffect(() => {
    setDraft(null);
    setEditing(false);
  }, [activeClinicId]);

  const modules = readActivatedModules(clinic?.activated_modules);
  const hasView = useCallback(
    (resource: PermissionResource) =>
      Boolean(activeClinicId && hasPermission(activeClinicId, resource, 'view')),
    [activeClinicId, hasPermission],
  );

  const allowedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const id of WIDGET_IDS) {
      if (isWidgetAllowed(WIDGET_CATALOG[id], hasView, modules)) ids.add(id);
    }
    return ids;
  }, [hasView, modules]);

  const baseLayout = draft ?? (hasSavedLayout ? storedLayout : DEFAULT_DASHBOARD_LAYOUT);
  const layoutRef = useRef(baseLayout);
  layoutRef.current = baseLayout;
  const visibleLayout = useMemo(
    () => withCatalogConstraints(filterLayoutByAccess(baseLayout, hasView, modules)),
    [baseLayout, hasView, modules],
  );
  const widgetLayout = useMemo(
    () => withCatalogConstraints(filterPageLevelWidgets(visibleLayout)),
    [visibleLayout],
  );

  const commit = useCallback(
    (nextVisible: readonly LayoutItem[], mode: 'save' | 'reset') => {
      const merged =
        mode === 'reset'
          ? buildResetLayout(layoutRef.current, DEFAULT_DASHBOARD_LAYOUT, allowedIds)
          : mergeVisibleLayout(layoutRef.current, nextVisible, allowedIds);
      layoutRef.current = merged;
      setDraft(merged);
      const write = mode === 'reset' ? resetLayout(merged) : saveLayout(merged);
      void write.catch(() => {
        if (mode === 'reset') setDraft(null);
        toast.error(t('dashboard.layout_save_error', 'Kunne ikke gemme dashboard'));
      });
    },
    [allowedIds, resetLayout, saveLayout, t],
  );

  const handleLayoutChange = useCallback(
    (next: Layout) => {
      if (!editing || !shouldPersistDashboardGridChange('layout-change')) return;
      commit(next, 'save');
    },
    [commit, editing],
  );

  const handleDragStop = useCallback(
    (next: Layout) => {
      if (!editing || !shouldPersistDashboardGridChange('drag-stop')) return;
      commit(next, 'save');
    },
    [commit, editing],
  );

  const handleResizeStop = useCallback(
    (next: Layout) => {
      if (!editing || !shouldPersistDashboardGridChange('resize-stop')) return;
      commit(next, 'save');
    },
    [commit, editing],
  );

  const handleRemove = useCallback(
    (id: string) => {
      if (!shouldPersistDashboardGridChange('remove')) return;
      commit(
        widgetLayout.filter((item) => item.i !== id),
        'save',
      );
    },
    [commit, widgetLayout],
  );

  const handleAdd = useCallback(
    (id: DashboardWidgetId) => {
      if (!shouldPersistDashboardGridChange('add')) return;
      const def = WIDGET_CATALOG[id];
      const next = [...widgetLayout, nextWidgetPlacement(widgetLayout, def)];
      commit(withCatalogConstraints(next), 'save');
      setPickerOpen(false);
    },
    [commit, widgetLayout],
  );

  const handleReset = useCallback(() => {
    if (!window.confirm(t('dashboard.reset_confirm', 'Gendan standard-dashboardet?'))) return;
    commit(DEFAULT_DASHBOARD_LAYOUT, 'reset');
  }, [commit, t]);

  const availableToAdd = WIDGET_IDS.filter(
    (id) =>
      allowedIds.has(id) && !isPageLevelWidget(id) && !widgetLayout.some((item) => item.i === id),
  );

  const displayCols = editing ? DASHBOARD_GRID_COLS : displayColsForWidth(width);
  const gridLayout = useMemo(
    () => (editing ? widgetLayout : layoutForDisplay(widgetLayout, displayCols)),
    [displayCols, editing, widgetLayout],
  );

  const today = useGreetingNow();
  const period = greetingPeriod(today);
  const greeting = t(`dashboard.greeting_${period}`, STAFF_GREETING_FALLBACK[period]);
  const userName = profile?.name?.trim() ?? '';
  const dateLabel = formatDashboardGreetingDate(today, i18n.language);
  const clinicName = clinic?.name?.trim();
  const subtitle = clinicName ? `${dateLabel} · ${clinicName}` : dateLabel;

  return (
    <PageLayout
      className="dashboard-page"
      title={
        <>
          {greeting}
          {userName ? `, ${userName}` : ''}
        </>
      }
      subtitle={subtitle}
      actions={
        <>
          {editing ? (
            <>
              <Button variant="secondary" onClick={() => setPickerOpen(true)}>
                <Plus />
                {t('dashboard.add_widget', 'Tilføj widget')}
              </Button>
              <Button variant="secondary" onClick={handleReset}>
                <RotateCcw />
                {t('dashboard.reset_layout', 'Nulstil')}
              </Button>
              <Button variant="secondary" onClick={() => setEditing(false)}>
                {t('dashboard.done', 'Færdig')}
              </Button>
            </>
          ) : (
            <Button variant="secondary" disabled={isLoading} onClick={() => setEditing(true)}>
              <LayoutGrid />
              {t('dashboard.customize', 'Tilpas')}
            </Button>
          )}
          <DashboardNewMenu clinicId={activeClinicId} />
        </>
      }
    >
      <div ref={containerRef as RefObject<HTMLDivElement>} className="min-w-0">
        {isLoading || !mounted ? (
          <div className="grid grid-cols-2 gap-s4 nav:grid-cols-4">
            <Skeleton className="h-[148px] rounded-card" />
            <Skeleton className="h-[148px] rounded-card" />
            <Skeleton className="h-[148px] rounded-card" />
            <Skeleton className="h-[148px] rounded-card" />
          </div>
        ) : widgetLayout.length === 0 ? (
          <EmptyState
            icon={<LayoutGrid className="h-7 w-7" />}
            title={t(
              'dashboard.no_widgets',
              'Ingen widgets endnu. Tilføj widgets, som din rolle har adgang til.',
            )}
            action={
              <Button
                variant="secondary"
                onClick={() => {
                  setEditing(true);
                  setPickerOpen(true);
                }}
              >
                <Plus />
                {t('dashboard.add_widget', 'Tilføj widget')}
              </Button>
            }
          />
        ) : (
          <ReactGridLayout
            width={width}
            layout={gridLayout}
            gridConfig={{
              cols: displayCols,
              rowHeight: DASHBOARD_ROW_HEIGHT,
              margin: DASHBOARD_GRID_MARGIN,
              containerPadding: [0, 0],
            }}
            dragConfig={{
              enabled: editing,
              handle: '.dashboard-widget-drag',
            }}
            resizeConfig={{ enabled: editing, handles: ['se'] }}
            onDragStop={handleDragStop}
            onResizeStop={handleResizeStop}
            onLayoutChange={handleLayoutChange}
            className={editing ? 'dashboard-grid-editing' : undefined}
          >
            {gridLayout.map((item) => (
              <div key={item.i} className="relative h-full min-h-0 min-w-0">
                <DashboardWidgetFrame className="h-full">
                  {editing && (
                    <div className="absolute left-s2 top-s2 z-10 flex items-center gap-s1">
                      <button
                        type="button"
                        className="dashboard-widget-drag cursor-grab rounded-ctl bg-ds-card p-s1 text-ds-muted shadow-card hover:text-ds-text"
                        aria-label={t('dashboard.drag_widget', 'Flyt widget')}
                      >
                        <GripVertical className="h-4 w-4" />
                      </button>
                      <Button
                        type="button"
                        variant="danger"
                        size="icon"
                        onClick={() => handleRemove(item.i)}
                        aria-label={t('dashboard.remove_widget', 'Fjern widget')}
                      >
                        <X />
                      </Button>
                    </div>
                  )}
                  <div className={cn('flex h-full min-h-0 flex-col', editing ? 'pt-s7' : '')}>
                    {isDashboardWidgetId(item.i) && (
                      <DashboardWidgetBody id={item.i} clinicId={activeClinicId} />
                    )}
                  </div>
                </DashboardWidgetFrame>
              </div>
            ))}
          </ReactGridLayout>
        )}
      </div>

      {pickerOpen && (
        <Modal
          title={t('dashboard.picker_title', 'Tilføj widget')}
          onClose={() => setPickerOpen(false)}
        >
          {availableToAdd.length === 0 ? (
            <p className={`${TYPE.body} text-ds-muted`}>
              {t(
                'dashboard.picker_empty',
                'Du har tilføjet alle widgets, din rolle har adgang til.',
              )}
            </p>
          ) : (
            <ul className="space-y-s2">
              {availableToAdd.map((id) => (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => handleAdd(id)}
                    className={cn(
                      TYPE.label,
                      'w-full rounded-ctl border border-ds-field px-s3 py-s3 text-left text-ds-text hover:bg-ds-sunk',
                    )}
                  >
                    {t(`dashboard.widgets.${WIDGET_CATALOG[id].nameKey}`)}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}
    </PageLayout>
  );
}
